/**
 * Test IDs for E2E testing.
 * These values are used as data-testid attributes throughout the application.
 * When writing E2E tests, import and use these enum values for consistent test selectors.
 */
export enum TestIds {
  // Accessibility
  SKIP_LINK = 'psp-skip-link',
  MAIN_CONTENT = 'psp-main-content',

  // Header component
  HEADER = 'psp-header',
  HAMBURGER_MENU = 'psp-hamburger-menu',
  USER_MENU_BUTTON = 'psp-user-menu-button',
  PROFILE_SETTINGS_BUTTON = 'psp-profile-settings-button',
  LOGOUT_BUTTON = 'psp-logout-button',
  LOGOUT_STATUS = 'psp-logout-status',
  LOGOUT_FAILED_DISMISS = 'psp-logout-failed-dismiss',
  NOTIFICATIONS_BELL = 'psp-notifications-bell',
  NOTIFICATIONS_POPOUT = 'psp-notifications-popout',
  NOTIFICATIONS_DRAWER = 'psp-notifications-drawer',
  NOTIFICATIONS_DRAWER_CLOSE_BUTTON = 'psp-notifications-drawer-close-button',
  NOTIFICATIONS_DRAWER_UNREAD_SWITCH = 'psp-notifications-drawer-unread-switch',
  NOTIFICATIONS_DRAWER_SORT_SELECT = 'psp-notifications-drawer-sort-select',
  OG_ASSIST_BUTTON = 'psp-og-assist-button',

  // Notification settings
  NOTIFICATION_SETTINGS_DIGEST_TIME = 'psp-notification-settings-digest-time',
  NOTIFICATION_SETTINGS_DELIVERY_MODE = 'psp-notification-settings-delivery-mode',
  NOTIFICATION_SETTINGS_DIGEST_FREQUENCY = 'psp-notification-settings-digest-frequency',
  NOTIFICATION_SETTINGS_DIGEST_DAY = 'psp-notification-settings-digest-day',

  // Digest email landing
  NOTIFICATION_DIGEST_BANNER = 'psp-notification-digest-banner',
  NOTIFICATION_CLEAR_FILTERS = 'psp-notification-clear-filters',

  // Navigation sidebar
  NAVIGATION_SIDEBAR = 'psp-navigation-sidebar',
  ENTITY_SELECTOR = 'psp-entity-selector',

  // Favorites (PSS-2642)
  FAVORITES_SECTION = 'psp-favorites-section',
  FAVORITES_SECTION_HEADER = 'psp-favorites-section-header',
  FAVORITES_MANAGE_BUTTON = 'psp-favorites-manage-button',
  FAVORITES_EMPTY_STATE = 'psp-favorites-empty-state',
  FAVORITES_EMPTY_ADD_BUTTON = 'psp-favorites-empty-add-button',
  FAVORITES_ROW = 'psp-favorites-row',
  FAVORITES_MANAGER = 'psp-favorites-manager',
  FAVORITES_MANAGER_ROW = 'psp-favorites-manager-row',
  FAVORITES_ADD_CURRENT_PAGE_BUTTON = 'psp-favorites-add-current-page-button',
  FAVORITES_ADD_DISMISS_BUTTON = 'psp-favorites-add-dismiss-button',
  FAVORITES_SAVE_BUTTON = 'psp-favorites-save-button',
  FAVORITES_ADD_SAVE_BUTTON = 'psp-favorites-add-save-button',
  FAVORITES_CLOSE_BUTTON = 'psp-favorites-close-button',
  FAVORITES_CURRENT_SECTION = 'psp-favorites-current-section',
  FAVORITES_ADD_SECTION = 'psp-favorites-add-section',

  // RemoteAppShell component
  LOADING_SPINNER = 'psp-loading-spinner',
  ERROR_PAGE = 'psp-error-page',
  REMOTE_APP_CONTAINER = 'psp-remote-app-container',

  // ProtectedRoute component (auth states)
  AUTH_LOADING = 'psp-auth-loading',
  AUTH_ERROR = 'psp-auth-error',
  AUTH_REDIRECTING = 'psp-auth-redirecting',

  // Task components (Command Center + Tasks Page)
  TASK_LIST_CARD = 'psp-task-list-card',
  TASK_LIST_ITEM = 'psp-task-list-item',
  TASK_EMPTY_STATE = 'psp-task-empty-state',
  VIEW_ALL_TASKS_BUTTON = 'psp-view-all-tasks-button',
  TASK_FILTER_TOOLBAR = 'psp-task-filter-toolbar',
  TASK_STATUS_FILTER = 'psp-task-status-filter',
  TASK_SEARCH_INPUT = 'psp-task-search-input',
  TASK_DRAWER = 'psp-task-drawer',
  TASK_ROW = 'psp-task-row',

  // Notification components (Notifications Page)
  NOTIFICATIONS_PAGE_CONTAINER = 'psp-notifications-page-container',
  NOTIFICATION_FILTER_TOOLBAR = 'psp-notification-filter-toolbar',
  NOTIFICATION_SEARCH_INPUT = 'psp-notification-search-input',
  NOTIFICATION_TYPE_FILTER = 'psp-notification-type-filter',
  NOTIFICATION_ROW = 'psp-notification-row',
  NOTIFICATION_PAGE_DRAWER = 'psp-notification-page-drawer',
  NOTIFICATION_INBOX_LAYOUT = 'psp-notification-inbox-layout',
  NOTIFICATION_SELECT_ALL = 'psp-notification-select-all',
  NOTIFICATION_BULK_READ_TOGGLE = 'psp-notification-bulk-read-toggle',
  NOTIFICATION_PRODUCT_FILTER_BUTTON = 'psp-notification-product-filter-button',
  NOTIFICATION_SORT_SELECT = 'psp-notification-sort-select',
  NOTIFICATION_UNREAD_SWITCH = 'psp-notification-unread-switch',
  NOTIFICATION_ROW_AVATAR = 'psp-notification-row-avatar',
  NOTIFICATION_ROW_OVERFLOW = 'psp-notification-row-overflow',
  NOTIFICATION_DRAWER_VIEW = 'psp-notification-drawer-view',
  NOTIFICATION_DRAWER_MARK_READ = 'psp-notification-drawer-mark-read',
  NOTIFICATION_DRAWER_FOOTER = 'psp-notification-drawer-footer',
  TASK_PRODUCT_FILTER_BUTTON = 'psp-task-product-filter-button',
  TASK_ROW_OVERFLOW = 'psp-task-row-overflow',
  TASK_DRAWER_VIEW = 'psp-task-drawer-view',
  TASK_DRAWER_FOOTER = 'psp-task-drawer-footer',

  // UnauthenticatedRemoteAppShell component
  UNAUTH_HEADER = 'psp-unauth-header',
  UNAUTH_REMOTE_APP_CONTAINER = 'psp-unauth-remote-app-container',
  UNAUTH_LOADING_SPINNER = 'psp-unauth-loading-spinner',
  UNAUTH_ERROR_PAGE = 'psp-unauth-error-page',

  // Chart components (used in CSS selectors)
  CHART_LEGEND = 'chart-legend',
  LEGEND_ITEM_DOT = 'legend-item-dot',

  // All Products page
  ALL_PRODUCTS_SEARCH_INPUT = 'psp-all-products-search-input',
  ALL_PRODUCTS_SORT_AZ = 'psp-all-products-sort-az',
  ALL_PRODUCTS_SORT_PINNED = 'psp-all-products-sort-pinned',
  ALL_PRODUCTS_GRID = 'psp-all-products-grid',
  ALL_PRODUCTS_VIEW_GRID = 'psp-all-products-view-grid',
  ALL_PRODUCTS_VIEW_TABLE = 'psp-all-products-view-table',
  ALL_PRODUCTS_TABLE = 'psp-all-products-table',

  // Command Center page
  COMMAND_CENTER_PAGE_CONTAINER = 'psp-command-center-page-container',

  // Drawer slots (in-flow assistant drawer region)
  DRAWER_SLOT_REGION = 'psp-drawer-slot-region',

  // Shared hovering overlay host (Notifications / Resource Center / remotes)
  OVERLAY_DRAWER_HOST = 'psp-overlay-drawer-host',

  // Shell-owned NavBar for the active routed remote (Phase 2 unified top nav)
  SHELL_REMOTE_NAV_BAR = 'psp-shell-remote-nav-bar',
}

/**
 * Prefixes for dynamically generated test IDs.
 * Use with helper functions below to create unique, consistent test IDs.
 */
export enum TestIdPrefixes {
  MENU_ITEM = 'menu-item',
  MENU_SECTION = 'menu-section',
}

/**
 * Generate a unique test ID for a menu item.
 * @param itemId - The unique identifier for the menu item (e.g., item.id or item.path)
 * @returns A formatted test ID string like "menu-item-command-center"
 */
export function getMenuItemTestId(itemId: string): string {
  const sanitizedId = itemId.replace(/^\/+/, '').replace(/\//g, '-');
  return `${TestIdPrefixes.MENU_ITEM}-${sanitizedId}`;
}

/**
 * Generate a unique test ID for a menu section.
 * @param sectionId - The unique identifier for the section
 * @returns A formatted test ID string like "menu-section-products"
 */
export function getMenuSectionTestId(sectionId: string): string {
  return `${TestIdPrefixes.MENU_SECTION}-${sectionId}`;
}
